import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {when} from 'lit/directives/when.js';
import {navigator} from 'lit-element-router';

import {ref, createRef} from 'lit/directives/ref.js';
import {msg} from '@lit/localize';

import store from '../store';
import {getProductData} from '../util/getProductData';
import {isSatisfied} from '../util/values';
import {featureInSection} from '../util/featureInSection';
import {regionCheck} from '../util/regionCheck';
import {selectCurrentSection, selectSectionConfig} from '../selectors';
import dot from '../../icons/dot';
import next from '../../icons/next';
import previous from '../../icons/previous';
import {setMedia, setSection} from '../slices/statusSlice';
import {routeUrl} from '../util/routeUrl';

@customElement('sections-tabs')
export class SectionsTabs extends connect(store)(navigator(LitElement)) {

	static styles = css`
		:host {
			background-color: transparent;
			z-index: 1;
			display: flex;
			flex-direction: row;
			gap: 8px;
		}

		.label {
			font: var(--font-widget-big-label);
			color: var(--color-widget-text);
		}

		.tabs {
			display: flex;
			gap: 8px;
			box-sizing: border-box;
			z-index: 10;
		}

		@media (max-width: 768px) {
			.label {
				max-width: 65px;
				text-overflow: ellipsis;
				overflow-x: hidden;
				white-space: nowrap;
			}
			.tabs {
				grid-template-columns: auto;
			}

			.tabs {
				box-sizing: border-box;
				width: 100%;
				display: grid;
				grid-auto-flow: column;
				column-gap: 3px;
				overflow-x: scroll;
				z-index: 10;
			}

			button {
				min-width: calc(100vw - var(--small-padding) * 2);
			}

		}

		.tabs::-webkit-scrollbar {
			display: none;
		}


		.icon {
			// margin-right: var(--very-small-padding);
			padding: 5px;
			line-height: 0;
			pointer-events: none;
			width: 11px;
			height: 11px;
			display: inline-block;
			color: var(--color-warn);
		}

		.icon svg * {
			display: block;
			fill: currentColor
		}

		.satisfied .icon {
			color: var(--color-grey5);
		}
    `;

	@property({type: String})
	currentSection = '';

	tabsRef = createRef();

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.productData = state.productData.productData;
		this.lang = state.status.lang;
		this.skipConfigurationEngine = state.status.skipConfigurationEngine;
		this.forceOverview = state.status.forceOverview;

		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.section = this.sectionConfig.find(s => s.id === this.currentSection);

		this.features = state.featureModel.data.features;
		this.decisions = state.decisions.data;
		this.featureModelIsSatisfied = state.decisions.data.isSatisfied;
		this.ensureTabInView(this.currentSectionIndex);

		this.lastSatisfiedIndex = 0;
		this.sectionConfig.some((section, index) => {
			this.lastSatisfiedIndex = index;
			return !this.isSectionSatisfied(section.id);
		});
	}

	visibleFeatures(sectionId) {
		if (sectionId) {
			const section = this.sectionConfig.find(s => s.id === sectionId);
			if (this.features && this.features.length > 0) {
				return this.features
					.filter(feature => featureInSection(this.sectionConfig, section, feature.variableId))
					.filter(feature => getProductData(feature.variableId, this.productData)?.isVisible)
					.filter(feature => regionCheck(getProductData(feature.variableId, this.productData), this.region));
			} else {
				return [];
			}
		} else {
			return [];
		}
	}
	ensureTabInView(index) {
		if (this.tabsRef.value) {
			const tab = this.tabsRef.value.children.item(index);
			const tabs = this.tabsRef.value;
			const offset = 32;
			tabs.scroll({left: tab.offsetLeft - offset, behavior: 'smooth'});
		}
	}

	isSectionSatisfied(sectionId) {
		if (sectionId) {
			const section = this.sectionConfig.find(s => s.id === sectionId);
			let customSectionSatisfied;
			let featuresOfSectionSatisfied;
			if(section.hasOwnProperty('isSatisfied')) {
				// result of custom function to check if the section is satisfied ...
				customSectionSatisfied = section.isSatisfied(this.state);
			} else {
				customSectionSatisfied = true;
			}
			if(section.hasOwnProperty('features') && section.features.length > 0) {
				// check if all features of the section are satisfied
				featuresOfSectionSatisfied = this.visibleFeatures(sectionId)
					.reduce((acc, feature) => acc && isSatisfied(feature.variableId, this.decisions), true);
			} else {
				featuresOfSectionSatisfied = true;
			}
			// return true if both conditions are satisfied
			return customSectionSatisfied && featuresOfSectionSatisfied;
		} else {
			return false;
		}
	}

	completionPercentage(sectionId) {
		const section = this.sectionConfig.find(s => s.id === sectionId);
		let visibleCount = this.visibleFeatures(sectionId).length ?? 100;
		let satisfiedCount = this.visibleFeatures(sectionId).filter(feature => isSatisfied(feature.variableId, this.decisions)).length ?? 0;

		if(section.hasOwnProperty('isSatisfied')) {
			visibleCount += 1;
			satisfiedCount += section.isSatisfied(this.state) ? 1 : 0;
		}

		return Math.round((satisfiedCount / visibleCount * 100));
	}

	featureModelCompletionPercentage() {
		const visibleCount = this.visibleFeatures().length ?? 100;
		const satisfiedCount = this.visibleFeatures().filter(feature => isSatisfied(feature.variableId, this.decisions)).length ?? 0;
		return Math.round((satisfiedCount / visibleCount * 100));
	}
	previousSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
		const newSection = this.sectionConfig[newIndex].id;

		store.dispatch(setSection({currentSection: newSection}));
		store.dispatch(setMedia({media: 'sections'}));
	}
	nextSection() {
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const newIndex = currentIndex < this.sectionConfig.length - 1 ? currentIndex + 1 : this.sectionConfig.length - 1;
		const newSection = this.sectionConfig[newIndex].id;

		if (currentIndex < this.sectionConfig.length - 1) {
			store.dispatch(setSection({currentSection: newSection}));
			store.dispatch(setMedia({media: 'sections'}));
		} else {
			if (this.state.status.family === 'LightProfile') {
				// TODO: not hardcoded configuration of summary/specsheet component
				this.navigate(routeUrl({media: this.state.status.media === 'summary' ? 'sections' : 'summary'}));
			} else {
				// this.navigate(routeUrl({media: 'specsheet'}));
			}

		}
	}

	render() {
		// if (!this.skipConfigurationEngine && (!this.features || this.features.length === 0)) return null;
		if (this.forceOverview) return null;
		const currentIndex = this.sectionConfig.findIndex(section => section.id === this.currentSection);
		const configurationDone = currentIndex >= this.lastSatisfiedIndex;

		return html`
			<div class="tabscontainer">
				<div class="label">
					<!--
					${when((this.isSectionSatisfied(this.currentSection)), () => html`
							<span class="icon">${dot}</span>
						`, () => html`
							<span class="icon">${dot}</span>
						`)}
					-->
					${this.section.label[this.lang]}
				</div>
				<nav class="tabs" ${ref(this.tabsRef)}>
					${this.sectionConfig
						.map((section, index) => html`
							<div class="tab">
								<sections-tab
									.disabled=${index > this.lastSatisfiedIndex}
									key=${section.id}
									label=${section.label[this.lang]}
									.percentage=${this.completionPercentage(section.id)}
									.satisfied=${this.isSectionSatisfied(section.id)}
									.selected=${section.id === this.currentSection}
								>
								</sections-tab>
							</div>
						`)}
				</nav>
			</div>
			<toolbar-button .icon=${previous} .disabled=${currentIndex === 0} .action=${this.previousSection.bind(this)}></toolbar-button>
			<toolbar-button .icon=${next}
							.disabled=${currentIndex >= this.lastSatisfiedIndex}
							.label=${msg('Next')}
							.action=${this.nextSection.bind(this)}></toolbar-button>

		`;
	}
}
